import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Tag, Typography } from 'antd';
import ReactMarkdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import 'katex/dist/katex.min.css';

const NEWS_MAPPING_EXPLANATION = String.raw`
The news-mapping component is an **ICS-weighted balance of precision and
recall** over headline-instrument pairs. It is not the raw percentage of
headlines mapped correctly.

For each candidate and assessment period, PM compares the candidate's active
news assignments with the benchmark ICS value for every benchmark headline and
eligible instrument.

### ICS and PCS

- **ICS — Individual Conviction Score:** one headline's signed contribution to
  the benchmark conviction for one instrument.
- **PCS — Period Conviction Score:** the overall benchmark conviction for that
  instrument across the period.

For each instrument, the individual headline contributions add up to its period
score:

$$
\sum_i \mathrm{ICS}_{i,j}=\mathrm{PCS}_{p,j}.
$$

### 1. Classify each pair

An instrument is materially affected by a headline when

$$
\left|\mathrm{ICS}_{n,i}\right| \ge 0.25.
$$

- **True positive (TP):** mapped and material.
- **False positive (FP):** mapped but not material.
- **False negative (FN):** not mapped but material.
- **True negative:** not mapped and not material; ignored by the score.

### 2. Weight the outcomes

Across all headline-instrument pairs in the period:

$$
W_{TP}=\sum_{\mathrm{TP}}\left|\mathrm{ICS}\right|
$$

$$
W_{FP}=\sum_{\mathrm{FP}}\max\!\left(\left|\mathrm{ICS}\right|,0.25\right)
$$

$$
W_{FN}=\sum_{\mathrm{FN}}\left|\mathrm{ICS}\right|
$$

Because an FP has $|\mathrm{ICS}|<0.25$, each false-positive link normally
adds a fixed $0.25$ penalty.

### 3. Balance precision and recall

$$
\mathrm{Precision}=\frac{W_{TP}}{W_{TP}+W_{FP}},
\qquad
\mathrm{Recall}=\frac{W_{TP}}{W_{TP}+W_{FN}}
$$

$$
M=\frac{2\,\mathrm{Precision}\,\mathrm{Recall}}
{\mathrm{Precision}+\mathrm{Recall}}
=\frac{2W_{TP}}{2W_{TP}+W_{FP}+W_{FN}}.
$$

Zero denominators produce zero, so making no mappings does not receive credit
for true negatives.

### 4. Convert the mapping score to Pillar 1 points

The news-mapping contribution for one period is

$$
\mathrm{MappingPoints}_p=40M_p.
$$

The complete period score is

$$
\begin{aligned}
\mathrm{P1}_p=100\bigl(&0.40M_p+0.25D_p\\
                       &+0.25C_p+0.10S_p\bigr),
\end{aligned}
$$

where $D$ is slider direction, $C$ is conviction calibration, and $S$ is
neutral/conflict selectivity. Final Pillar 1 is the arithmetic mean of the
period scores.

### Worked example

Suppose one period produces $W_{TP}=0.80$, $W_{FP}=0.25$, and
$W_{FN}=0.30$:

$$
M=\frac{2(0.80)}{2(0.80)+0.25+0.30}=0.7442
$$

$$
\mathrm{MappingPoints}=40(0.7442)=29.77.
$$

> Mapping measures whether the candidate identified a relationship. The sign
> of ICS does not affect this component; direction is assessed through the
> conviction-slider components.
`;

const PillarOneScoreGuide: React.FC = () => (
  <aside
    aria-labelledby="pillar-one-news-mapping-title"
    className="assessment-inspect-assessment__pillar-one-guide"
  >
    <header className="assessment-inspect-assessment__pillar-one-guide-header">
      <div className="assessment-inspect-assessment__pillar-one-guide-kicker">
        <span>
          <InfoCircleOutlined aria-hidden="true" /> Scoring guide
        </span>
        <Tag color="blue">40% of Pillar 1</Tag>
      </div>
      <Typography.Title id="pillar-one-news-mapping-title" level={4}>
        News-mapping score
      </Typography.Title>
      <Typography.Text type="secondary">
        How candidate links become up to 40 points in each period.
      </Typography.Text>
    </header>

    <div className="assessment-inspect-assessment__pillar-one-guide-body">
      <ReactMarkdown rehypePlugins={[rehypeKatex]} remarkPlugins={[remarkMath]}>
        {NEWS_MAPPING_EXPLANATION}
      </ReactMarkdown>
    </div>
  </aside>
);

export default PillarOneScoreGuide;
